import {
    Button, 
    FormControl, 
    FormHelperText, 
    Grid, 
    Stack, 
    Typography,
    Select,
    MenuItem,
    SelectChangeEvent,
    TextField,
    Alert,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Radio
} from '@mui/material'
import React, {useContext, useEffect, useState} from 'react'
import {AppContext} from '../../../contexts/AppContextProvider'
import {CancelButton, PreviousButton} from '../../StepperButtons'
import {ComplaintTypeState, LocationOptions, ProductTypeOptions, YesNoOptions,} from '../../../contexts/types'
import {Trans} from 'react-i18next'
import productTypeIsOneOf from '../../../lib/productTypeIsOneOf'
import {useForm} from '../../../hooks/useForm'
import isSameDay from 'date-fns/isSameDay'
import {OrgCustomizationContext} from "../../../themes/OrgCustomizationProvider";
import {setComplaintType} from "../../../contexts/ComplaintTypeReducer";
import useTheHackiestPageNavigation from "../../../hooks/useTheHackiestPageNavigation";
import {db} from "../../../lib/db";
import makeStyles from '@mui/styles/makeStyles'
import createStyles from '@mui/styles/createStyles'
import {Theme} from '@mui/material/styles'
import MultiImageInput from "../../Inputs/MultiImageInput"

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formControl: {
            minWidth: 300,
            marginBottom: theme.spacing(2),
        },
    })
)

// Define complaint categories
const COMPLAINT_CATEGORIES = {
    NONE: '',
    OSSEOINTEGRATION: 'Osseointegration complaints',
    LABEL_PACKAGING: 'Labeling / Packaging complaints', 
    FIT: 'Assembly complaints',
    DAMAGE_FRACTURE: 'Damage / Fracture complaints',
    ESTHETIC: 'Esthetic complaints',
    IFU: 'Instructions for Use (IFU) complaints',
    SURFACE: 'Surface complaints',
    MRI_COMPATIBILITY: 'MRI compatibility complaints',
    OTHER: 'Other complaints'
};

const ComplaintType: React.FC = () => {
    const classes = useStyles()
    const {navigateForward} = useTheHackiestPageNavigation()
    const {complaint, dispatch, postNotification, clearNotification, countryCode} = useContext(AppContext)
    const {t, tns, orgIdentifier, language} = useContext(OrgCustomizationContext)
    const [category, setCategory] = useState(COMPLAINT_CATEGORIES.NONE)

    const productType = complaint.generalComplaint
        .productType as ProductTypeOptions

    const shouldShowLackOfStabilityOption = isSameDay(
        complaint.complaintType.dateOfOccurrence as Date,
        complaint.treatmentDates.dateOfTreatment as Date
    )

    const required = {value: true, message: t('this_field_is_required')}
    const {
        data,
        errors,
        handleChange,
        validateFields,
        enableOnChangeValidation,
        setFormData
    } = useForm<ComplaintTypeState>({
        initialValues: {
            ...complaint.complaintType,
            complaintType:
                !shouldShowLackOfStabilityOption &&
                complaint.complaintType.complaintType === 'LACK_STABILITY'
                    ? ''
                    : complaint.complaintType.complaintType,
        },
        validations: {
            complaintType: {required},
            estheticExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.complaintType === 'OTHER_OUTCOME' &&
                            !data.estheticExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            otherExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.complaintType === 'OTHER_DEFICIENCIES' &&
                            !data.otherExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            surfaceDefectExplanation: {
                required: {
                    value: (value, data) => {
                        return !(
                            data.complaintType === 'SURFACE_DEFECT' &&
                            !data.surfaceDefectExplanation
                        )
                    },
                    message: t('this_field_is_required'),
                },
            },
            attachmentIds: {
                required: {
                    value: (value, data) => {
                        // Only require attachments when the image upload section is shown
                        // (Nobel org and non-Japan customers)
                        if (orgIdentifier !== 'Nobel' || countryCode === 'jp') {
                            return true
                        }
                        return !(['WRONG_PART_PACKAGED', 'DAMAGE_WITH_PACKAGING_INTACT'].includes(data.complaintType) && !data.attachmentIds?.length)
                    },
                    message: t('this_field_is_required')
                },
            }
        },
    })
    
    useEffect( () => {
        if (!['WRONG_PART_PACKAGED', 'DAMAGE_WITH_PACKAGING_INTACT'].includes(data.complaintType) && data.attachmentIds.length > 0) {
            db.attachments.bulkDelete(data.attachmentIds)
            setFormData({...data, attachmentIds: []})
        }
    }, [data.complaintType])

    const wasPatientInvolved = complaint.generalComplaint.wasPatientInvolved
    const eventOccurrence = complaint.complaintType.location

    // Set the initial category based on the selected complaint type
    useEffect(() => {
        if (data.complaintType) {
            if (['WITHIN_365_DAYS', 'MORE_THAN_365_DAYS', 'LACK_STABILITY', 'PART_MIGRATES'].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.OSSEOINTEGRATION)
            } else if ([
                'FOREIGN_PARTICLES', 'LABEL_ILLEGIBLE', 'LABEL_MISLEADING', 'LABEL_INFORMATION_MISSING',
                'WRONG_PART_PACKAGED', 'STERILE_BARRIER_DAMAGED', 'DAMAGE_WITH_PACKAGING_INTACT',
                'PART_DISASSEMBLED', 'INADEQUATE_CLEANING_OF_PART'
            ].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.LABEL_PACKAGING)
            } else if ([
                'CLINICAL_PROSTHETIC_SCREW_LOOSENING', 'FUNCTIONALITY_COMPROMISED', 'PART_DISASSEMBLY_ISSUE',
                'PART_ASSEMBLY_ISSUE', 'DOES_NOT_ENGAGE', 'DOES_NOT_DISENGAGE', 'DOES_NOT_FIT', 'PART_NOT_RETAINED'
            ].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.FIT)
            } else if (['PART_FRACTURED', 'NOT_FUNCTIONAL', 'DEFORMED', 'PREMATURE_WEAR'].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.DAMAGE_FRACTURE)
            } else if (['COLOR_MISMATCHING', 'SHINES_THROUGH', 'PARTIALLY_VISIBLE', 'OTHER_OUTCOME'].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.ESTHETIC)
            } else if (['TRANSLATION_MISSING', 'INFORMATION_MISLEADING', 'WRONG_PART', 'UNABLE_TO_DISTINGUISH'].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.IFU)
            } else if (['DISCOLORATION', 'SURFACE_DEFECT', 'SURFACE_CORRODED', 'COATING_FLAKING_OFF', 'INTERFACE_FRETTING'].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.SURFACE)
            } else if (['ARTIFIACTS_ON_IMAGE', 'DEFLECTION', 'HEATING'].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.MRI_COMPATIBILITY)
            } else if (['TRAUMA', 'OTHER_DEFICIENCIES'].includes(data.complaintType)) {
                setCategory(COMPLAINT_CATEGORIES.OTHER)
            } else {
                setCategory(COMPLAINT_CATEGORIES.NONE)
            }
        }
    }, [data.complaintType])

    const handleCategoryChange = (event: SelectChangeEvent) => {
        const selectedCategory = event.target.value
        setCategory(selectedCategory)
        // Reset complaint type when changing category
        handleChange('complaintType', '')
    }

    const handleComplaintTypeChange = (event: SelectChangeEvent) => {
        handleChange('complaintType', event.target.value)
    }

    const handleTextFieldChange = (
        fieldName: keyof ComplaintTypeState,
        value: string
    ) => {
        handleChange(fieldName, value)
    }

    // Helper function to get available categories based on conditions
    const getAvailableCategories = () => {
        const categories = []

        // Osseointegration category
        if (wasPatientInvolved === 'YES' &&
            productTypeIsOneOf(['IMPLANT'], productType) &&
            (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
            eventOccurrence === '5_CLINIC_AFTER_PROCEDURE') &&
            !(wasPatientInvolved === 'YES' && complaint.generalComplaint.wasProductPlaced === 'NO')) {
            categories.push({
                value: COMPLAINT_CATEGORIES.OSSEOINTEGRATION,
                label: t(countryCode === 'jp' && language === 'ja' ? 'cti_complaint_osseointegration_jp' : 'cti_complaint_osseointegration')
            })
        }

        // Label/Packaging category
        if (eventOccurrence === '1_LAB_INITIAL_INSPECTION' ||
            eventOccurrence === '2_LAB_DURING_FINALIZATION' ||
            eventOccurrence === '3_CLINIC_INITIAL_INSPECTION') {
            categories.push({
                value: COMPLAINT_CATEGORIES.LABEL_PACKAGING,
                label: t('cti_complaint_labeling_packaging')
            })
        }

        // Fit category - always available
        categories.push({
            value: COMPLAINT_CATEGORIES.FIT,
            label: t('cti_complaint_fit')
        })

        // Damage/Fracture category - always available
        categories.push({
            value: COMPLAINT_CATEGORIES.DAMAGE_FRACTURE,
            label:  t(countryCode === 'jp' && language === 'ja' ? 'cti_complaint_damage_jp' : 'cti_complaint_damage')
        })

        // Esthetic category
        if ((productTypeIsOneOf(['ABUTMENT'], productType) ||
            (productTypeIsOneOf(['IMPLANT'], productType) && wasPatientInvolved === 'YES')) &&
            !(wasPatientInvolved === 'YES' && complaint.generalComplaint.wasProductPlaced === 'NO')) {
            categories.push({
                value: COMPLAINT_CATEGORIES.ESTHETIC,
                label: t('cti_complaint_esthetic')
            })
        }

        // IFU category
        // For IMDI, occurrence 4 ("clinic during procedure") only offered WRONG_PART,
        // which has been removed, so the IFU category is no longer shown there for IMDI.
        if (eventOccurrence === '1_LAB_INITIAL_INSPECTION' ||
            eventOccurrence === '2_LAB_DURING_FINALIZATION' ||
            eventOccurrence === '3_CLINIC_INITIAL_INSPECTION' ||
            (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' && orgIdentifier !== 'IMDI')) {
            categories.push({
                value: COMPLAINT_CATEGORIES.IFU,
                label: t('cti_complaint_ifu')
            })
        }

        // Surface category - always available
        categories.push({
            value: COMPLAINT_CATEGORIES.SURFACE,
            label: t('cti_complaint_surface')
        })

        // MRI Compatibility category
        if (wasPatientInvolved === 'YES' &&
            productTypeIsOneOf(['IMPLANT', 'ABUTMENT', 'SCREW'], productType) &&
            (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
            eventOccurrence === '5_CLINIC_AFTER_PROCEDURE')) {
            categories.push({
                value: COMPLAINT_CATEGORIES.MRI_COMPATIBILITY,
                label: t('cti_complaint_mri_combatibility')
            })
        }

        // Other category - conditional availability
        if (orgIdentifier !== 'Nobel' || 
            (orgIdentifier === 'Nobel' && wasPatientInvolved === 'YES' &&
            (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
            eventOccurrence === '5_CLINIC_AFTER_PROCEDURE'))) {
            categories.push({
                value: COMPLAINT_CATEGORIES.OTHER,
                label: t('cti_complaint_other')
            })
        }

        return categories
    }

    // Helper function to get complaint type options for selected category
    const getComplaintTypeOptions = () => {
        if (category === COMPLAINT_CATEGORIES.NONE) {
            return []
        }

        switch (category) {
            case COMPLAINT_CATEGORIES.OSSEOINTEGRATION:
                return [
                    { value: 'WITHIN_365_DAYS', label: t('cti_option_within_365_days') },
                    { value: 'MORE_THAN_365_DAYS', label: t('cti_option_more_than_365_days') },
                    ...(shouldShowLackOfStabilityOption ? [{ value: 'LACK_STABILITY', label: t('cti_option_lack_stability') }] : []),
                    { value: 'PART_MIGRATES', label: t(countryCode === 'jp' && language === 'ja' ? 'cti_option_part_migrates_jp' : 'cti_option_part_migrates') }
                ]

            case COMPLAINT_CATEGORIES.LABEL_PACKAGING:
                const labelOptions = [
                    { value: 'FOREIGN_PARTICLES', label: t('cti_option_foreign_particles') },
                    { value: 'LABEL_ILLEGIBLE', label: t('cti_option_label_illegible') },
                    { value: 'LABEL_MISLEADING', label: t('cti_option_label_misleading') },
                    { value: 'LABEL_INFORMATION_MISSING', label: t('cti_option_label_information_missing') },
                    { value: 'WRONG_PART_PACKAGED', label: t('cti_option_wrong_part_packaged') },
                    { value: 'STERILE_BARRIER_DAMAGED', label: t('cti_option_sterile_barrier_damaged') },
                    { value: 'DAMAGE_WITH_PACKAGING_INTACT', label: t('cti_option_damage_with_packaging_intact') },
                    { value: 'PART_DISASSEMBLED', label: t('cti_option_part_disassembled') }
                ]
                
                // Add INADEQUATE_CLEANING_OF_PART for specific product types and locations
                if (productTypeIsOneOf(['ABUTMENT', 'CLINICAL_INSTRUMENT', 'LAB_INSTRUMENT_LAB_SCREW'], productType) &&
                    (eventOccurrence === '1_LAB_INITIAL_INSPECTION' ||
                    eventOccurrence === '2_LAB_DURING_FINALIZATION')) {
                    labelOptions.push({ value: 'INADEQUATE_CLEANING_OF_PART', label: t('cti_option_inadequate_cleaning_of_part') })
                }
                
                return labelOptions

            case COMPLAINT_CATEGORIES.FIT:
                const fitOptions = [
                    { value: 'PART_NOT_RETAINED', label: t('cti_option_part_not_retained') },
                    { value: 'DOES_NOT_ENGAGE', label: t('cti_option_does_not_engage') },
                    { value: 'DOES_NOT_DISENGAGE', label: t('cti_option_does_not_disengage') },
                    { value: 'DOES_NOT_FIT', label: t('cti_option_does_not_fit') }
                ]
                
                // Add FUNCTIONALITY_COMPROMISED for instruments only
                if (productTypeIsOneOf(['CLINICAL_INSTRUMENT', 'LAB_INSTRUMENT_LAB_SCREW'], productType)) {
                    fitOptions.unshift({ value: 'FUNCTIONALITY_COMPROMISED', label: t('cti_option_functionality_compromised') })
                }
                
                // Add CLINICAL_PROSTHETIC_SCREW_LOOSENING for abutments/screws in clinic
                if (productTypeIsOneOf(['ABUTMENT', 'SCREW'], productType) &&
                    (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
                    eventOccurrence === '5_CLINIC_AFTER_PROCEDURE')) {
                    fitOptions.push({ value: 'CLINICAL_PROSTHETIC_SCREW_LOOSENING', label: t('cti_option_clinical_prosthetic_screw_loosening') })
                }
                
                // Add assembly/disassembly options for specific product types
                if (productTypeIsOneOf(['ABUTMENT', 'SCREW', 'CLINICAL_INSTRUMENT', 'LAB_INSTRUMENT_LAB_SCREW'], productType)) {
                    fitOptions.push(
                        { value: 'PART_DISASSEMBLY_ISSUE', label: t('cti_option_part_disassembly_issue') },
                        { value: 'PART_ASSEMBLY_ISSUE', label: t('cti_option_part_assembly_issue') }
                    )
                }
                
                return fitOptions

            case COMPLAINT_CATEGORIES.DAMAGE_FRACTURE:
                const options = [
                    { value: 'NOT_FUNCTIONAL', label: t('cti_option_not_functional') },
                    { value: 'DEFORMED', label: t('cti_option_deformed') }
                ]
                
                // Add PART_FRACTURED only if patient was involved and in clinic
                if (wasPatientInvolved === 'YES' &&
                    (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
                    eventOccurrence === '5_CLINIC_AFTER_PROCEDURE')) {
                    options.unshift({ value: 'PART_FRACTURED', label: t('cti_option_part_fractured') })
                }
                
                // Add PREMATURE_WEAR for instruments
                if (['CLINICAL_INSTRUMENT', 'LAB_INSTRUMENT_LAB_SCREW'].includes(productType || '')) {
                    options.push({ value: 'PREMATURE_WEAR', label: t('cti_option_premature_wear') })
                }
                
                return options

            case COMPLAINT_CATEGORIES.ESTHETIC:
                const estheticOptions = []
                
                // COLOR_MISMATCHING only for abutments
                if (productTypeIsOneOf(['ABUTMENT'], productType)) {
                    estheticOptions.push({ value: 'COLOR_MISMATCHING', label: t('cti_option_color_mismatching') })
                }
                
                // These options only available if patient was involved and in clinic procedures
                if (wasPatientInvolved === 'YES' &&
                    (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
                    eventOccurrence === '5_CLINIC_AFTER_PROCEDURE')) {
                    estheticOptions.push(
                        { value: 'SHINES_THROUGH', label: t('cti_option_shines_through', {productType: t(`pt_option_${productType.toLowerCase()}`)}) },
                        { value: 'PARTIALLY_VISIBLE', label: t('cti_option_partially_visible', {productType: t(`pt_option_${productType.toLowerCase()}`)}) },
                        { value: 'OTHER_OUTCOME', label: t('cti_option_other_outcome') }
                    )
                }
                
                return estheticOptions

            case COMPLAINT_CATEGORIES.IFU:
                const ifuOptions = []
                
                // These options only for lab and initial clinic inspection
                if (eventOccurrence === '1_LAB_INITIAL_INSPECTION' ||
                    eventOccurrence === '2_LAB_DURING_FINALIZATION' ||
                    eventOccurrence === '3_CLINIC_INITIAL_INSPECTION') {
                    ifuOptions.push(
                        { value: 'TRANSLATION_MISSING', label: t('cti_option_translation_missing') },
                        { value: 'INFORMATION_MISLEADING', label: t('cti_option_information_misleading') },
                        { value: 'UNABLE_TO_DISTINGUISH', label: t('cti_option_unable_to_distinguish') }
                    )
                }
                
                // WRONG_PART also available during clinic procedures
                // Not offered for IMDI ("Usability - wrong part used" removed from the IMDI claim form)
                if (orgIdentifier !== 'IMDI' &&
                    (eventOccurrence === '1_LAB_INITIAL_INSPECTION' ||
                    eventOccurrence === '2_LAB_DURING_FINALIZATION' ||
                    eventOccurrence === '3_CLINIC_INITIAL_INSPECTION' ||
                    eventOccurrence === '4_CLINIC_DURING_PROCEDURE')) {
                    ifuOptions.push({ value: 'WRONG_PART', label: t('cti_option_wrong_part') })
                }
                
                return ifuOptions

            case COMPLAINT_CATEGORIES.SURFACE:
                const surfaceOptions = [
                    { value: 'DISCOLORATION', label: t('cti_option_discoloration') },
                    { value: 'SURFACE_CORRODED', label: t('cti_option_surface_corroded') },
                    { value: 'COATING_FLAKING_OFF', label: t('cti_option_coating_flaking_off') },
                    { value: 'SURFACE_DEFECT', label: t('cti_option_surface_defect') }
                ]
                
                // Add INTERFACE_FRETTING for specific product types only
                if (productTypeIsOneOf(['ABUTMENT', 'SCREW', 'CLINICAL_INSTRUMENT', 'LAB_INSTRUMENT_LAB_SCREW'], productType)) {
                    surfaceOptions.push({ value: 'INTERFACE_FRETTING', label: t('cti_option_interface_fretting') })
                }
                
                return surfaceOptions

            case COMPLAINT_CATEGORIES.MRI_COMPATIBILITY:
                return [
                    { value: 'ARTIFIACTS_ON_IMAGE', label: t('cti_option_artifiacts_on_image') },
                    { value: 'DEFLECTION', label: t('cti_option_deflection') },
                    { value: 'HEATING', label: t('cti_option_heating') }
                ]

            case COMPLAINT_CATEGORIES.OTHER:
                const otherOptions = []
                
                // TRAUMA only for patient involved cases in clinic procedures
                if (wasPatientInvolved === 'YES' &&
                    (eventOccurrence === '4_CLINIC_DURING_PROCEDURE' ||
                    eventOccurrence === '5_CLINIC_AFTER_PROCEDURE')) {
                    otherOptions.push({ value: 'TRAUMA', label: t('cti_option_trauma') })
                }
                
                // OTHER_DEFICIENCIES only for non-Nobel organizations
                if (orgIdentifier !== 'Nobel') {
                    otherOptions.push({ value: 'OTHER_DEFICIENCIES', label: t('cti_option_other_deficiencies') })
                }
                
                return otherOptions

            default:
                return []
        }
    }

    // Render additional fields based on the selected complaint type
    const renderAdditionalFields = () => {
        switch (data.complaintType) {
            case 'OTHER_OUTCOME':
                return (
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={4}
                        sx={{ maxWidth: '40rem', mt: 2 }}
                        name='estheticExplanation'
                        error={!!errors.estheticExplanation}
                        helperText={errors.estheticExplanation}
                        value={data.estheticExplanation}
                        onChange={(e) => handleTextFieldChange('estheticExplanation', e.target.value)}
                        placeholder={t('cti_please_explain')}
                    />
                )
            case 'OTHER_DEFICIENCIES':
                return (
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={4}
                        sx={{ maxWidth: '40rem', mt: 2 }}
                        name='otherExplanation'
                        error={!!errors.otherExplanation}
                        helperText={errors.otherExplanation}
                        value={data.otherExplanation}
                        onChange={(e) => handleTextFieldChange('otherExplanation', e.target.value)}
                        placeholder={t('cti_please_explain')}
                    />
                )
            case 'SURFACE_DEFECT':
                return (
                    <TextField
                        multiline
                        minRows={2}
                        maxRows={4}
                        sx={{ maxWidth: '40rem', mt: 2 }}
                        name='surfaceDefectExplanation'
                        error={!!errors.surfaceDefectExplanation}
                        helperText={errors.surfaceDefectExplanation}
                        value={data.surfaceDefectExplanation}
                        onChange={(e) => handleTextFieldChange('surfaceDefectExplanation', e.target.value)}
                        placeholder={t('cti_please_explain')}
                    />
                )
            case 'WRONG_PART_PACKAGED':
            case 'DAMAGE_WITH_PACKAGING_INTACT':
                return orgIdentifier === 'Nobel' && countryCode !== 'jp'  ? (
                    <Stack direction='column' spacing={1} sx={{ mt: 2 }}>
                        {data.attachmentIds?.length === 0 && (
                            <Alert severity='info'>
                                {t('cti_please_include_pictures_of_packaging')}
                            </Alert>
                        )}
                        <MultiImageInput 
                            attachmentIds={data.attachmentIds}
                            setAttachmentIds={(attachmentIds: number[]) => setFormData({...data, attachmentIds})}
                            tag='productComplaint'
                            errorText={errors.attachmentIds}
                        />
                    </Stack>
                ) : null
            
            case 'STERILE_BARRIER_DAMAGED':
                return (
                    <Alert severity='warning' sx={{ mt: 2 }}>
                        {t('cti_not_applicable_warning')}
                    </Alert>
                )
            default:
                return null
        }
    }

    const handleSubmit = async () => {
        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        const isFormValid = validateFields(data)

        // Stop processing if the form has invalid fields
        if (!isFormValid) {
            postNotification('error', t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        // Package up the form data and update app state
        const complaintType: ComplaintTypeState = {
            ...data,
            estheticExplanation:
                data.complaintType === 'OTHER_OUTCOME'
                    ? data.estheticExplanation
                    : '',
            otherExplanation:
                data.complaintType === 'OTHER_DEFICIENCIES'
                    ? data.otherExplanation
                    : '',
            surfaceDefectExplanation:
                data.complaintType === 'SURFACE_DEFECT'
                    ? data.surfaceDefectExplanation
                    : '',
        }

        dispatch(setComplaintType(complaintType))

        navigateForward()
    }

    return (
        <Grid container>
            <Stack spacing={2} sx={{width: '100%'}}>
                <Typography variant='h5' sx={{mb: 4}}>
                    {t('cti_complaint_type')} -{' '}
                    {t(`pt_option_${productType.toLowerCase()}`).toLowerCase()}
                </Typography>

                

                {/* Category dropdown */}
                <FormControl className={classes.formControl}>
                    <Typography variant="h6" gutterBottom>
                        {t('cti_select_type_of_complaint')}
                    </Typography>
                    <Typography variant='body2' color='textSecondary' gutterBottom>
                       {t('cti_complaint_category_instruction')}
                    </Typography>
                    <Select
                        value={category}
                        onChange={handleCategoryChange}
                        displayEmpty
                    >
                        <MenuItem value={COMPLAINT_CATEGORIES.NONE}>
                            <em>-- Select --</em>
                        </MenuItem>
                        {getAvailableCategories().map(cat => (
                            <MenuItem key={cat.value} value={cat.value}>
                                {cat.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Complaint type dropdown */}
                {category !== COMPLAINT_CATEGORIES.NONE && (
                    <FormControl className={classes.formControl}>
                        <Typography variant='h6' gutterBottom>
                            {getAvailableCategories().find(cat => cat.value === category)?.label || category}
                        </Typography>
                        <Select
                            value={data.complaintType}
                            onChange={handleComplaintTypeChange}
                            error={!!errors.complaintType}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>-- Select --</em>
                            </MenuItem>
                            {getComplaintTypeOptions().map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <FormHelperText error={!!errors.complaintType}>
                            {errors.complaintType}
                        </FormHelperText>
                    </FormControl>
                )}

                {/* Additional fields based on selected complaint type */}
                {renderAdditionalFields()}

                {/* Show packaging note for certain complaint types */}
                {['WRONG_PART_PACKAGED', 'DAMAGE_WITH_PACKAGING_INTACT'].includes(data.complaintType) && countryCode !== 'jp' && (
                    <Typography variant='caption' color='textSecondary'>
                        * {t('cti_not_applicable_warning')}
                    </Typography>
                )}
            </Stack>

            <Stack direction='row-reverse' spacing={2} sx={{pt: 4, width: '100%'}}>
                <Button
                    variant='contained'
                    color='primary'
                    onClick={() => handleSubmit()}
                >
                    {t('continue')}
                </Button>

                <PreviousButton before={() => dispatch(setComplaintType(data))}/>

                <CancelButton/>
            </Stack>
        </Grid>
    )
}

export default ComplaintType